import { apiFunctionsEnum } from 'constants/api.constants';
import ApiService from './api.service';
import { setErrorMessage } from 'utils/index';
import _ from 'lodash';
import {
	setCategories,
	setLoading,
	setError,
	setCurrentCategory,
	setTranslationsMap,
	selectPaymentCategoryTranslation,
} from 'state/reducers/payment-categories.reducer';
import { store } from 'state/store';
import { IPaymentCategoryFormData } from 'constants/payment-categories.constants';
import type { PaymentCategoryOption } from 'components/PaymentCategoryPicker/types';

/**
 * Translation record interface for payment category translations
 */
interface ITranslationRecord {
	key?: string;
	en?: string;
	he?: string;
}

/**
 * Normalize an array of translation records into a key -> { en, he } map.
 * Ignores records without a key.
 */
const normalizeTranslationsMap = (records: ITranslationRecord[]): Record<string, { en?: string; he?: string }> => {
	return (records || []).reduce((acc, item) => {
		if (!item?.key) return acc;
		acc[item.key] = { en: item.en, he: item.he };
		return acc;
	}, {} as Record<string, { en?: string; he?: string }>);
};

/**
 * Fetch all payment categories as a tree structure
 */
export function getPaymentCategories() {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_PAYMENT_CATEGORIES, {})
			.then((response: any) => {
				dispatch(setCategories(_.get(response.data, 'categories', [])));
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

/**
 * Fetch a single category (always includes translations)
 */
export function getPaymentCategory(id: number) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		let result = null;
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_PAYMENT_CATEGORY, { id })
			.then((response: any) => {
				result = _.get(response.data, 'category', null);
				dispatch(setCurrentCategory(result));
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
		return result;
	};
}

/**
 * Create a new payment category (supports optional translation fields)
 */
export function createPaymentCategory(data: IPaymentCategoryFormData) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		let success = false;
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.CREATE_PAYMENT_CATEGORY, data)
			.then(() => {
				success = true;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
		return success;
	};
}

/**
 * Update an existing payment category (supports optional translation fields)
 */
export function updatePaymentCategory(id: number, data: IPaymentCategoryFormData) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		let success = false;
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.UPDATE_PAYMENT_CATEGORY, { id, ...data })
			.then(() => {
				success = true;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
		return success;
	};
}

/**
 * Toggle payment category active status
 */
export function togglePaymentCategoryActive(id: number, isActive: boolean) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		let success = false;
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.TOGGLE_PAYMENT_CATEGORY_ACTIVE, { id, isActive })
			.then(() => {
				success = true;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
		return success;
	};
}

/**
 * Fetch all payment category translations in bulk (via content/feature/pages) and store as a map
 */
export function fetchPaymentCategoryTranslations() {
	return async (dispatch: any) => {
		let result = {};
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.PAYMENT_CATEGORIES_TRANSLATIONS, {
				category: 'open system ui',
				feature: 'payment categories',
			})
			.then((response: any) => {
				const rawPages = _.get(response.data, 'pages', []);
				const items: any[] = [];
				rawPages.forEach((page: any) => {
					(page.sections || []).forEach((section: any) => {
						(section.fieldGroups || []).forEach((fg: any) => {
							(fg.fieldGroupItems || []).forEach((item: any) => {
								items.push(item);
							});
						});
					});
				});

				// Fallback: if no pages returned, leave result empty (UI will show keys)
				result = normalizeTranslationsMap(items);
				dispatch(setTranslationsMap(result));
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			});
		return result;
	};
}

/**
 * Sync all active payment categories to all companies
 */
export function syncPaymentCategoriesToCompanies() {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		let result = null;
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.SYNC_PAYMENT_CATEGORIES_TO_COMPANIES, {})
			.then((response: any) => {
				result = _.get(response, 'data', null);
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
		return result;
	};
}

// ============================================================================
// Payment Category Utilities
// ============================================================================

/**
 * Formats a payment category option into a human-readable display string
 * with hierarchy separated by slashes (e.g., "Primary / Secondary / Tertiary")
 *
 * @param option - Payment category option object containing primary, secondary, tertiary fields
 * @param fallbackId - Fallback ID to use if option is not provided
 * @param categoryTranslation - Optional translation function for category names (like t() for CSV)
 * @returns Formatted string with translated category names separated by " / "
 */
export const formatPaymentCategory = (
	option?: PaymentCategoryOption,
	fallbackId?: string,
	categoryTranslation?: (categoryName: string, fallback?: string) => string
): string => {
	if (!option && !fallbackId) return '';

	const primary = option?.primary || option?.label || fallbackId || '';
	const secondary = option?.secondary?.trim();
	const tertiary = option?.tertiary
		?.split(',')
		.map((s) => s.trim())
		.filter(Boolean)?.[0];

	const parts: string[] = [];

	// Use categoryTranslation function if provided, otherwise fallback to raw name
	if (primary) {
		parts.push(categoryTranslation ? categoryTranslation(primary) : primary);
	}
	if (secondary && secondary !== primary) {
		parts.push(categoryTranslation ? categoryTranslation(secondary) : secondary);
	}
	if (tertiary && tertiary !== primary && tertiary !== secondary) {
		parts.push(categoryTranslation ? categoryTranslation(tertiary) : tertiary);
	}

	return parts.join(' / ');
};

/**
 * Creates a map of payment category options indexed by their ID
 */
export const createCategoryMap = (options: PaymentCategoryOption[]): Record<string, PaymentCategoryOption> => {
	return options.reduce((acc, option) => {
		acc[option.id] = option;
		return acc;
	}, {} as Record<string, PaymentCategoryOption>);
};

/**
 * Find a category by ID in the tree and return its path from root to the category.
 * This function traverses all categories (including parents and inactive) to find the category.
 *
 * @param categories - The category tree to search
 * @param targetId - The ID of the category to find
 * @returns Array of category names representing the path from root to the target, or null if not found
 */
export const findCategoryPathById = (categories: any[], targetId: number | string): string[] | null => {
	const targetIdStr = String(targetId);

	const findPath = (nodes: any[], path: string[]): string[] | null => {
		if (!nodes || !Array.isArray(nodes)) return null;

		for (const node of nodes) {
			const currentName = node.name || node.categoryName || '';
			const currentPath = [...path, currentName];

			// Found the target
			if (String(node.id) === targetIdStr) {
				return currentPath;
			}

			// Search children
			const children = node.children || [];
			if (children.length > 0) {
				const found = findPath(children, currentPath);
				if (found) return found;
			}
		}

		return null;
	};

	return findPath(categories, []);
};

/**
 * Get the formatted payment category display string for a given category ID.
 * Accesses Redux store to retrieve category tree and Hebrew translations.
 * Works for ANY category (leaf or parent, active or inactive).
 *
 * Uses selectPaymentCategoryTranslation to translate category names to Hebrew
 * (falls back to raw name if translation not found).
 *
 * @param categoryId - The ID of the payment category to display
 * @returns Formatted Hebrew string with category hierarchy (e.g., "ראשי / משני / שלישי")
 */
export const getPaymentCategoryDisplayById = (categoryId: number | string | null | undefined): string => {
	if (!categoryId) return '';

	const paymentCategoriesState = store.getState().paymentCategories;
	const categories = paymentCategoriesState.categories || [];
	const categoryTranslation = selectPaymentCategoryTranslation({ paymentCategories: paymentCategoriesState });

	// Find the path from root to this category (works for any category - leaf, parent, active, inactive)
	const path = findCategoryPathById(categories, categoryId);

	if (!path || path.length === 0) {
		// Category not found in tree - return the ID as fallback
		return String(categoryId);
	}

	// Translate each part of the path and join with " / "
	const translatedParts = path.map((name) => (categoryTranslation ? categoryTranslation(name) : name));
	return translatedParts.join(' / ');
};

/**
 * Flattens a hierarchical payment category tree into a flat array of options
 * suitable for the picker dropdown.
 * @param categories - The category tree to flatten
 * @param includeInactive - If true, includes inactive categories (for display views). Default false (for pickers).
 */
export const flattenPaymentCategories = (categories: any[], includeInactive = false): PaymentCategoryOption[] => {
	const result: PaymentCategoryOption[] = [];

	const traverse = (nodes: any[], primary?: string, secondary?: string) => {
		if (!nodes || !Array.isArray(nodes)) return;

		for (const node of nodes) {
			// Skip inactive categories unless includeInactive is true
			if (!includeInactive && node.isActive === false) continue;

			const currentName = node.name || node.categoryName || '';
			const children = node.children || [];

			if (children.length === 0) {
				// Leaf node - add to results
				result.push({
					id: String(node.id),
					label: currentName,
					primary: primary || currentName,
					secondary: secondary || (primary ? currentName : undefined),
					tertiary: primary && secondary ? currentName : undefined,
				});
			} else {
				// Has children - recurse
				const newPrimary = primary || currentName;
				const newSecondary = primary ? currentName : undefined;
				traverse(children, newPrimary, newSecondary);
			}
		}
	};

	traverse(categories);
	return result;
};
